import Cookies from "js-cookie";

import { toGetCartProductsItem } from "~/pagesSrrLogic/cart/toGetCartProductsItem";
import { getCookieDomain } from "~/utils";
import { getLostGiftIds } from "~/utils/cartGifts";
import { safeLocalStorage } from "~/utils/safeStorage";

import { ApiClient } from "../../api/client";

/**
 * Экшены корзины (basket). Вынесены из action-creaters/cart.js, чтобы вся
 * логика загрузки/кеширования корзины жила в одном месте.
 *
 * Ключевая цель — не долбить бэкенд Bitrix (/basket/getBasket) на каждой
 * навигации. Раньше getBacket форсировал живой запрос при каждом переходе,
 * что при трафике из поиска давало постоянный поток тяжёлых сессионных
 * запросов и таймауты (см. GlitchTip FRONTEND-9U6). Теперь корзина берётся из
 * localStorage-кеша, а живой запрос идёт только когда это реально нужно
 * (см. getBacket).
 */

const BASKET_CACHE_KEY = "basket";
/** Время жизни кеша корзины в localStorage — 1 минута. */
const BASKET_CACHE_TTL_MS = 60 * 1000;
/**
 * Пауза после неудачного запроса корзины.
 *
 * Без неё неудача сама себя размножает: кеш при таймауте не пишется (данных
 * нет), а getBacket дёргается на каждой смене роута — значит на каждой
 * следующей странице снова живой запрос, снова таймаут и снова событие в
 * GlitchTip. Один посетитель с обрывающимся мобильным каналом так давал 11
 * событий basket.getBasket за 7 минут (24.08, 14:11–14:18), тогда как getUser
 * с тем же обрывом дал одно: он кеширует и неудачу.
 */
const BASKET_FAILURE_BACKOFF_MS = 60 * 1000;

/**
 * Момент последней неудачи (таймаут/обрыв). Живёт в памяти вкладки: пауза
 * нужна ровно на время сессии с плохой связью, переживать перезагрузку ей
 * незачем.
 */
let lastBasketFailureTs = 0;

/**
 * Поддомен, на котором корзину читали в последний раз. Кука на .farkop.ru, то
 * есть общая для всех поддоменов.
 *
 * localStorage у msk.farkop.ru, spb.farkop.ru и farkop.ru раздельный, а корзина
 * в Bitrix одна: запросы всегда идут на https://farkop.ru/bxApi (см.
 * api/client/index.ts), сессия живёт в куке на .farkop.ru. Поэтому набранная на
 * msk корзина не видна в кеше farkop.ru, а там лежит своя старая запись — при
 * смене города пользователь видел пустую корзину.
 *
 * Кука отличается от текущего хоста — значит корзину читали/меняли на другом
 * поддомене, и местный кеш заведомо неверен: перезапрашиваем сразу, не
 * дожидаясь TTL.
 */
const BASKET_LAST_DOMAIN_COOKIE = "basketDomain";

/** Поддомен последнего чтения/сброса корзины (или "" — неизвестно). */
function readLastBasketDomain() {
  return Cookies.get(BASKET_LAST_DOMAIN_COOKIE) || "";
}

/** Запомнить текущий поддомен: кеши остальных становятся неактуальными. */
function writeLastBasketDomain() {
  if (typeof window === "undefined") return;
  try {
    Cookies.set(BASKET_LAST_DOMAIN_COOKIE, window.location.hostname, {
      domain: getCookieDomain(),
      path: "/",
      expires: 1,
    });
  } catch (e) {
    // Куки недоступны — остаётся обычный TTL.
  }
}

/**
 * Сбросить кеш корзины на всех поддоменах.
 *
 * Нужен там, где корзина на бэкенде заведомо изменилась не через наши экшены:
 *  - оформлен заказ — корзина Bitrix очищена (см. useCartOrderLogic);
 *  - вход/выход из аккаунта — Bitrix подставляет корзину пользователя
 *    (см. common/auth, action-creaters/user).
 * Без этого шапка до конца TTL показывает уже оформленные или чужие товары.
 *
 * Кеш соседних поддоменов сбрасывает запись текущего домена в куку: для них он
 * становится чужим.
 */
export function clearBasketCache() {
  try {
    safeLocalStorage.removeItem(BASKET_CACHE_KEY);
  } catch (e) {
    // no-op
  }
  writeLastBasketDomain();
}

/** Прочитать кеш корзины из localStorage. Возвращает { data, ts } или null. */
function readBasketCache() {
  try {
    const raw = safeLocalStorage.getItem(BASKET_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || typeof parsed.ts !== "number") {
      return null;
    }
    return parsed;
  } catch (e) {
    return null;
  }
}

/** Записать корзину в кеш localStorage с текущей меткой времени. */
function writeBasketCache(data) {
  try {
    safeLocalStorage.setItem(
      BASKET_CACHE_KEY,
      JSON.stringify({ data, ts: Date.now() }),
    );
  } catch (e) {
    // localStorage может быть недоступен/переполнен — не критично.
  }
  // Для остальных поддоменов этот кеш станет чужим — они перезапросят корзину.
  writeLastBasketDomain();
}

/**
 * Живая загрузка корзины со всеми данными для отрисовки:
 *  1) getBasket — сама корзина Bitrix (ITEMS);
 *  2) cart.getCartProducts — PRODUCT_DATA (имя, URL, картинка, раздел) по каждому
 *     товару + фолбэк картинок; всё одним запросом (см. pages/api/client/cart).
 * Возвращает корзину или null (если бэкенд не ответил — clientApiCall вернёт null).
 */
async function fetchBasketFull() {
  const basket = await ApiClient.basket.getBasket();
  const items = basket?.ITEMS;
  if (!Array.isArray(items) || items.length === 0) {
    return basket;
  }

  const cartItemsPayload = items.map(toGetCartProductsItem);
  const productsDataList = await ApiClient.cart.getCartProducts(cartItemsPayload);

  return {
    ...basket,
    ITEMS: items.map((item, index) => ({
      ...item,
      PRODUCT_DATA: Array.isArray(productsDataList)
        ? productsDataList[index] || null
        : null,
    })),
  };
}

/**
 * Загрузить корзину.
 *
 * Живой запрос в бэкенд идёт ТОЛЬКО если:
 *   - force === true — совершено действие с корзиной (добавить/удалить/изменить),
 *     открыта страница оформления заказа или показан попап «товар добавлен»;
 *   - в localStorage нет кеша корзины;
 *   - кеш старше BASKET_CACHE_TTL_MS (1 минута);
 *   - кеш остался от другого поддомена (переключили город, см.
 *     BASKET_LAST_DOMAIN_COOKIE) или был сброшен заказом/входом в аккаунт.
 * Иначе (обычная навигация по сайту) корзина берётся из кеша без запроса.
 *
 * Плюс пауза после неудачи: если прошлый запрос не дошёл, следующие
 * BASKET_FAILURE_BACKOFF_MS обычная навигация запрос не повторяет (см.
 * BASKET_FAILURE_BACKOFF_MS). Действия с корзиной (force) идут всегда.
 *
 * @param {boolean} [force=false]
 */
export const getBacket = (force = false) => {
  return async (dispatch, getState) => {
    const cache = readBasketCache();
    // Последний раз корзину читали на другом поддомене — местный кеш устарел.
    // Показывать его нельзя даже временно: там заведомо не те товары.
    const lastDomain = readLastBasketDomain();
    const isStale =
      !cache ||
      (Boolean(lastDomain) && lastDomain !== window.location.hostname);
    const isFresh = !isStale && Date.now() - cache.ts < BASKET_CACHE_TTL_MS;

    // Обычная навигация со свежим кешем — отдаём из кеша, бэкенд не трогаем.
    if (!force && isFresh) {
      dispatch({ type: "SET_BASKET", payload: cache.data });
      dispatch({ type: "SET_BASKET_LOADED", payload: true });
      return;
    }

    // Недавняя неудача — не долбим бэкенд на каждой странице. Показываем то,
    // что есть в кеше (даже протухшее), это честнее пустой корзины.
    if (!force && Date.now() - lastBasketFailureTs < BASKET_FAILURE_BACKOFF_MS) {
      if (cache && !isStale) {
        dispatch({ type: "SET_BASKET", payload: cache.data });
      }
      dispatch({ type: "SET_BASKET_LOADED", payload: true });
      return;
    }

    dispatch({ type: "SET_BASKET_LOADING", payload: true });

    // Пока грузим свежие данные — сразу показываем то, что есть в кеше.
    if (cache && !isStale) {
      dispatch({ type: "SET_BASKET", payload: cache.data });
    }

    const prevBasket = getState()?.cart?.basket;
    let basket = await fetchBasketFull();

    // Подарок без последнего фаркопа Bitrix оставляет в корзине платным — убираем.
    const lostGiftIds = getLostGiftIds(prevBasket, basket);
    if (lostGiftIds.length > 0) {
      await Promise.all(
        lostGiftIds.map((id) => ApiClient.basket.removeFromBasket(id)),
      );
      basket = await fetchBasketFull();
    }

    // null означает, что бэкенд не ответил (таймаут). Не затираем кеш и не
    // показываем пустую корзину — оставляем то, что уже отрисовано.
    if (basket) {
      lastBasketFailureTs = 0;
      writeBasketCache(basket);
      dispatch({ type: "SET_BASKET", payload: basket });
    } else {
      lastBasketFailureTs = Date.now();
    }

    dispatch({ type: "SET_BASKET_LOADING", payload: false });
    dispatch({ type: "SET_BASKET_LOADED", payload: true });
  };
};
